import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useNotifications } from '../../contexts/NotificationContext'
import { useHaptics } from '../../utils/haptics'
import { usePublicSettings, useMoney } from '../../hooks/usePublicSettings'
import { bookingApi, addressLine, moneyRange, formatYmd, callOutCreditText, bookingConflict, pricedForText } from './bookingApi'
import { BookingConflictPanel, PaymentInstructions } from './BookingNotices'

// Icons
function ArrowLeftIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M19 12H5M12 19l-7-7 7-7"/>
    </svg>
  )
}

function MapPinIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  )
}

function CalendarIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="3" y="4" width="18" height="18" rx="2"/>
      <path d="M16 2v4M8 2v4M3 10h18"/>
    </svg>
  )
}

function ClockIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="12" cy="12" r="10"/>
      <path d="M12 6v6l4 2"/>
    </svg>
  )
}

function EditIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  )
}

function CheckCircleIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <path d="M9 11l3 3L22 4"/>
    </svg>
  )
}

export default function BookingReview() {
  const { professionalId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { showError, showSuccess } = useNotifications()
  const haptics = useHaptics()
  const settings = usePublicSettings()
  const money = useMoney()

  const [professional, setProfessional] = useState(null)
  const [selectedAddress, setSelectedAddress] = useState(null)
  const [addresses, setAddresses] = useState([])
  const [quote, setQuote] = useState(null)
  const [specialInstructions, setSpecialInstructions] = useState('')
  const [serviceDescription, setServiceDescription] = useState('')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [conflict, setConflict] = useState(null)   // refused booking (409) the customer can act on

  // Booking details from the date/time step (at = slot ISO time or 'asap')
  const bookingDetails = location.state || {}
  const isAsap = bookingDetails.at === 'asap'
  const asapMinutes = settings?.['booking.asap_lead_minutes']
  const visitMinutes = settings?.['booking.slot_minutes']

  useEffect(() => {
    if (!bookingDetails.at) {
      showError('Missing booking information. Please select date and time again.')
      navigate(`/booking/datetime/${professionalId}`, { replace: true })
      return
    }
    loadData()
  }, [])

  const quoteParams = { professionalId, at: bookingDetails.at, trade: bookingDetails.trade, severity: bookingDetails.severity, method: bookingDetails.method, quantity: bookingDetails.quantity }

  const loadData = async () => {
    try {
      const [pro, addrs, q] = await Promise.all([
        bookingApi.professional(professionalId),
        bookingApi.addresses().catch(() => []),
        bookingApi.quote(quoteParams)
      ])
      setProfessional(pro)
      const list = Array.isArray(addrs) ? addrs : []
      setAddresses(list)
      setSelectedAddress(list.find(a => a.is_default) || list[0] || null)
      setQuote(q)
    } catch (error) {
      console.error('Error loading booking details:', error)
      showError(error.message || 'Failed to load booking details')
    } finally {
      setLoading(false)
    }
  }

  // callOutFee = the fee the customer agreed to: the quote shown, or the new fee they accepted after a 409
  const handleSubmitBooking = async (callOutFee = quote?.callOut?.fee) => {
    if (!selectedAddress) {
      showError('Please add a service address first')
      return
    }
    if (!serviceDescription.trim()) {
      showError('Please describe the service you need')
      return
    }

    setSubmitting(true)
    setConflict(null)
    haptics.light()

    try {
      // Creates the service request + job and offers it to the pro (same as the WhatsApp flow)
      const booking = await bookingApi.book({
        professionalId,
        at: bookingDetails.at,
        addressId: selectedAddress.id,
        description: serviceDescription.trim(),
        specialInstructions: specialInstructions.trim(),
        trade: bookingDetails.trade,
        method: bookingDetails.method,
        quantity: bookingDetails.quantity,
        severity: bookingDetails.severity,
        callOutFee
      })

      haptics.success()
      showSuccess(`Booking created! Job #${booking.jobNumber}`)
      navigate('/booking/confirmation', {
        state: {
          bookingId: booking.jobNumber,
          booking,
          professional,
          ...bookingDetails,
          address: selectedAddress,
          serviceDescription,
          specialInstructions
        }
      })
    } catch (error) {
      console.error('Error creating booking:', error)
      haptics.error()
      const c = bookingConflict(error)
      if (c) setConflict(c)   // shown above the action bar: accept the new fee or pick another time
      else showError(error.message || 'Failed to create booking. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  // Accept the new call-out fee: refresh the price shown, then book at the fee the customer accepted
  const handleAcceptFee = async () => {
    const accepted = conflict?.callOutFee
    bookingApi.quote(quoteParams).then(setQuote).catch(() => {})
    await handleSubmitBooking(accepted)
  }

  // Changing the time keeps what the booking is for (trade, severity, method), so the new quote prices the same case
  const carry = { trade: bookingDetails.trade, severity: bookingDetails.severity, method: bookingDetails.method, quantity: bookingDetails.quantity }
  const pickAnotherTime = () => navigate(`/booking/datetime/${professionalId}`, { state: carry })

  const fee = quote?.callOut
  const inCurrency = { currency: quote?.currency }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white px-4 py-4 shadow-lg">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-slate-100 rounded-xl touch-manipulation"
          >
            <ArrowLeftIcon />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Review Booking</h1>
            <p className="text-sm text-slate-500">Confirm your service details</p>
          </div>
        </div>
      </div>

      <div className="px-4 py-6 space-y-6 pb-24">
        {/* Professional Info */}
        <div className="bg-white rounded-2xl p-5 shadow-lg">
          <h3 className="font-semibold text-slate-900 mb-3">Professional</h3>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center">
              <span className="text-sm font-semibold text-slate-600">
                {professional?.name?.split(' ').map(n => n[0]).join('')}
              </span>
            </div>
            <div className="flex-1">
              <div className="font-medium text-slate-900">{professional?.name}</div>
              <div className="text-sm text-slate-600">{professional?.company}</div>
              <div className="text-sm text-slate-500">
                Rating: {professional?.rating != null ? `${Number(professional.rating).toFixed(1)}/5` : 'No reviews yet'}
                {professional?.hourlyRate && ` • ${moneyRange(money, professional.hourlyRate)}/hour`}
              </div>
            </div>
            <button
              onClick={() => navigate(`/professionals/${professionalId}`, { state: carry })}
              className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg touch-manipulation"
            >
              <EditIcon />
            </button>
          </div>
        </div>

        {/* Date & Time */}
        <div className="bg-white rounded-2xl p-5 shadow-lg">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-900">Date & Time</h3>
            <button
              onClick={pickAnotherTime}
              className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg touch-manipulation"
            >
              <EditIcon />
            </button>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <CalendarIcon className="text-slate-500" />
              <div>
                <div className="font-medium text-slate-900">
                  {isAsap ? 'Emergency Service' : formatYmd(bookingDetails.date)}
                </div>
                <div className="text-sm text-slate-600">
                  {isAsap ? (asapMinutes != null ? `Visit targeted within ${asapMinutes} minutes` : 'As soon as possible') :
                   bookingDetails.urgency === 'today' ? 'Same day service' : 'Scheduled appointment'}
                </div>
              </div>
            </div>
            
            {!isAsap && (
              <div className="flex items-center gap-3">
                <ClockIcon className="text-slate-500" />
                <div>
                  <div className="font-medium text-slate-900">{bookingDetails.timeDisplay || bookingDetails.time}</div>
                  <div className="text-sm text-slate-600">{visitMinutes != null ? `Inspection visit: ${visitMinutes} minutes` : ''}{bookingDetails.rate ? ` • ${bookingDetails.rate}` : ''}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Service Address */}
        <div className="bg-white rounded-2xl p-5 shadow-lg">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-900">Service Address</h3>
            <button
              onClick={() => navigate('/addresses')}
              className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg touch-manipulation"
            >
              <EditIcon />
            </button>
          </div>
          
          <div className="space-y-3">
            {addresses.length === 0 && (
              <button
                onClick={() => navigate('/addresses')}
                className="w-full p-3 rounded-xl border-2 border-dashed border-slate-300 text-sm text-slate-600 hover:bg-slate-50 touch-manipulation"
              >
                No saved addresses yet — add one to book
              </button>
            )}
            {addresses.map((address) => (
              <button
                key={address.id}
                onClick={() => setSelectedAddress(address)}
                className={`w-full p-3 rounded-xl border-2 text-left transition-all touch-manipulation ${
                  selectedAddress?.id === address.id
                    ? 'border-slate-900 bg-slate-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <MapPinIcon className="text-slate-500 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="font-medium text-slate-900">{address.label}</div>
                    <div className="text-sm text-slate-600">
                      {addressLine(address)}
                    </div>
                  </div>
                  {selectedAddress?.id === address.id && (
                    <CheckCircleIcon className="text-green-600" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Service Description */}
        <div className="bg-white rounded-2xl p-5 shadow-lg">
          <h3 className="font-semibold text-slate-900 mb-3">Service Description *</h3>
          <textarea
            value={serviceDescription}
            onChange={(e) => setServiceDescription(e.target.value)}
            placeholder="Describe the service you need (e.g., 'Kitchen sink is leaking from the faucet base, started yesterday')"
            className="w-full h-24 px-3 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent resize-none"
            required
          />
          <p className="text-xs text-slate-500 mt-2">
            Be specific to help the professional prepare and provide accurate pricing
          </p>
        </div>

        {/* Special Instructions */}
        <div className="bg-white rounded-2xl p-5 shadow-lg">
          <h3 className="font-semibold text-slate-900 mb-3">Special Instructions</h3>
          <textarea
            value={specialInstructions}
            onChange={(e) => setSpecialInstructions(e.target.value)}
            placeholder="Any special instructions, access codes, parking info, etc. (optional)"
            className="w-full h-20 px-3 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent resize-none"
          />
        </div>

        {/* Price from the server (pricing rules: rate band, emergency/severity surcharges) */}
        <div className="bg-slate-50 rounded-2xl p-5">
          <h3 className="font-semibold text-slate-900 mb-3">Cost Estimate</h3>
          {!quote ? (
            <div className="text-sm text-slate-500">Price not available</div>
          ) : (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-start">
                <div>
                  <span className="text-slate-600">{isAsap ? 'Emergency call-out fee' : 'Call-out fee'}</span>
                  <div className="text-xs text-slate-500 mt-0.5">{fee.rate}</div>
                  {pricedForText(quote) && <div className="text-xs text-slate-500 mt-0.5">Priced for: {pricedForText(quote)}</div>}
                  <div className={`text-xs mt-0.5 ${fee.deductible ? 'text-green-600' : 'text-slate-500'}`}>
                    {callOutCreditText(fee.deductible)}
                  </div>
                </div>
                <span className="font-medium text-slate-900">{money(fee.fee, inCurrency)}</span>
              </div>

              {quote.estimate && (
                <div className="flex justify-between items-start">
                  <div>
                    <span className="text-slate-600">{quote.estimate.method}</span>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {quote.estimate.quantity} {quote.estimate.unit} • {quote.estimate.source === 'rate_card' ? "professional's price list" : 'labour estimate'}
                      {!quote.estimate.includesMaterials && ' • materials extra'}
                    </div>
                  </div>
                  <span className="font-medium text-slate-900">{money(quote.estimate.amount, inCurrency)}</span>
                </div>
              )}

              {quote.platformFee.included && quote.platformFee.pct > 0 && (
                <div className="flex justify-between text-xs text-slate-500">
                  <span>Platform fee ({quote.platformFee.pct}%, included)</span>
                  <span>{money(quote.platformFee.amount, inCurrency)}</span>
                </div>
              )}

              <div className="border-t border-slate-200 pt-2 mt-3">
                <div className="flex justify-between">
                  <span className="font-semibold text-slate-900">Due for the visit</span>
                  <span className="font-bold text-slate-900">{money(quote.total, inCurrency)}</span>
                </div>
              </div>
            </div>
          )}
          <p className="text-xs text-slate-500 mt-3">
            * The repair is quoted by the professional after the inspection. You'll be notified of any cost before work begins.
            {quote && ` Free cancellation up to ${quote.freeCancellationHours} hours before the visit.`}
          </p>
        </div>

        <PaymentInstructions text={quote?.payment?.instructions} />

        <BookingConflictPanel
          conflict={conflict}
          money={money}
          busy={submitting}
          onAcceptFee={handleAcceptFee}
          onPickAnother={pickAnotherTime}
        />

        {/* Terms */}
        <div className="bg-blue-50 rounded-2xl p-4">
          <p className="text-sm text-slate-700">
            By confirming this booking, you agree to our{' '}
            <button className="text-slate-900 font-medium underline" disabled title="Not available yet">Terms of Service</button>
            {' '}and{' '}
            <button className="text-slate-900 font-medium underline" disabled title="Not available yet">Cancellation Policy</button>.
          </p>
        </div>
      </div>

      {/* Bottom Action Bar */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 safe-area-inset">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <button
            onClick={() => handleSubmitBooking()}
            disabled={submitting || !serviceDescription.trim() || !selectedAddress || !quote}
            className={`w-full py-3 rounded-xl font-medium touch-manipulation transition-all ${
              submitting || !serviceDescription.trim() || !selectedAddress || !quote
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-slate-900 text-white hover:bg-slate-800 shadow-md'
            }`}
          >
            {submitting ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Creating Booking...
              </div>
            ) : (
              `Confirm Booking${quote ? ` • ${money(quote.total, inCurrency)}` : ''}`
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
